import { gql, useMutation } from "@apollo/client";
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";
import { createContext, useContext, useEffect, useState } from "react";
import { loginRequest, msalConfig } from "../config/msalConfig";
import { StorageHelper } from "../utils/storageHelper";

const LOGIN_WITH_MICROSOFT = gql`
  mutation LoginWithMicrosoft($idToken: String!) {
    loginWithMicrosoft(idToken: $idToken) {
      accessToken
      userDetails
      dbDetails
    }
  }
`;

export const AuthContext = createContext(null);
const msalInstance = new PublicClientApplication(msalConfig);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => StorageHelper.get("token"));
  const [user, setUser] = useState(() => StorageHelper.get("userInfo"));
  const [isInitialized, setIsInitialized] = useState(false);
  const [loginWithMicrosoft] = useMutation(LOGIN_WITH_MICROSOFT);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();
        const storedToken = StorageHelper.get("token");
        const storedUser = StorageHelper.get("userInfo");

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
        } else if (accounts.length > 0) {
          const userInfo = {
            ...accounts[0],
            token: accounts[0]?.token,
          };
          setUser(userInfo);
          setToken(accounts[0]?.token);
          StorageHelper.set("token", accounts[0]?.token);
          StorageHelper.set("userInfo", userInfo);
        }
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize MSAL:", error);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // Handle token changes
  useEffect(() => {
    if (token) {
      StorageHelper.set("token", token);
    }
  }, [token]);

  const login = async (newToken, userData) => {
    console.log("Setting token:", newToken);
    if (newToken) {
      setToken(newToken);
      setUser(userData);
      StorageHelper.set("token", newToken);
      StorageHelper.set("userInfo", userData);
    }
  };

  const logout = async () => {
    try {
      await msalInstance.logoutPopup();
    } catch (error) {
      console.error("Error during logout:", error);
    }

    console.log("Clearing token and user data");
    setToken(null);
    setUser(null);
    StorageHelper.remove("token");
    StorageHelper.remove("userInfo");
    StorageHelper.remove("refreshToken");
    StorageHelper.remove("schemaInfo");
    StorageHelper.remove("permissions");
  };

  const loginWithMicrosoftFn = async () => {
    try {
      if (!isInitialized) {
        throw new Error("Authentication is not initialized yet");
      }

      const response = await msalInstance.loginPopup(loginRequest);
      console.log("MSAL Response:", response);

      if (response) {
        const idToken = response.idToken;
        const result = await loginWithMicrosoft({
          variables: { idToken },
        });

        if (result.data?.loginWithMicrosoft) {
          const { accessToken, userDetails,dbDetails } = result.data.loginWithMicrosoft;
          localStorage.setItem("token", accessToken);
          localStorage.setItem("userDetails", JSON.stringify(userDetails));
          if(dbDetails !== "")
           localStorage.setItem('selected_user_db_id', dbDetails);
          localStorage.setItem("snowflake_datasource_id", dbDetails);
          const userInfo = {
            ...response.account,
            token: accessToken,
            userDetails: userDetails,
          };

          await login(accessToken, userInfo);

          window.dispatchEvent(new Event("storage"));

          return {
            success: true,
            data: userInfo,
          };
        }
      }
      throw new Error("Login failed - No response from server");
    } catch (error) {
      console.error("Microsoft Login Error:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  const acquireToken = async () => {
    if (!isInitialized) {
      throw new Error("Authentication is not initialized yet");
    }
    const account = msalInstance.getAllAccounts()[0];
    if (!account) {
      throw new Error("No active account");
    }
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await msalInstance.acquireTokenPopup(loginRequest);
        return response.accessToken;
      }
      throw error;
    }
  };

  if (!isInitialized) {
    return null;
  }

  const value = {
    token,
    user,
    login,
    logout,
    loginWithMicrosoftFn,
    acquireToken,
    isLoggedIn: !!token,
    isInitialized,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
